import React from 'react';
import { Card, CardHeader, CardContent } from '../../../components/ui/card';
import { TrendingUp, TrendingDown, Activity, DollarSign, Target, BarChart3 } from 'lucide-react';

interface Props {
  data: any[];
  loading: boolean;
}

const PerformanceCharts: React.FC<Props> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-400">Ładowanie danych wydajności...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-zinc-900/50 rounded-xl p-8 border border-zinc-700/50 text-center">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400">Brak danych do analizy</p>
        <p className="text-sm text-gray-500 mt-2">Uruchom audyt aby zobaczyć wykresy wydajności</p>
      </div>
    );
  }

  // Calculate metrics
  const scoreDistribution = {
    promote: data.filter(k => k.totalScore >= 70).length,
    test: data.filter(k => k.totalScore >= 40 && k.totalScore < 70).length,
    exclude: data.filter(k => k.totalScore < 40).length
  };

  const totalKeywords = data.length;
  const avgCTR = (data.reduce((acc, k) => acc + k.metrics.ctr, 0) / data.length * 100).toFixed(2);
  const avgCVR = (data.reduce((acc, k) => acc + k.metrics.cvr, 0) / data.length * 100).toFixed(2);
  const avgROAS = (data.reduce((acc, k) => acc + k.metrics.roas, 0) / data.length).toFixed(2);
  const totalCost = Math.round(data.reduce((acc, k) => acc + k.metrics.costMicros, 0) / 1000000);
  const totalConversions = data.reduce((acc, k) => acc + k.metrics.conversions, 0);
  const totalClicks = data.reduce((acc, k) => acc + k.metrics.clicks, 0);

  const top5Keywords = [...data]
    .sort((a, b) => b.totalScore - a.totalScore)
    .slice(0, 5);

  const bottom5Keywords = [...data]
    .sort((a, b) => a.totalScore - b.totalScore)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Main Stats Grid - DARK THEME */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-white/70">Średni CTR</span>
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Activity className="w-4 h-4 text-blue-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{avgCTR}%</div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-xs text-white/50">+12% vs poprzedni miesiąc</span>
          </div>
        </div>

        <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-white/70">Średni CVR</span>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Target className="w-4 h-4 text-green-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{avgCVR}%</div>
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-xs text-white/50">+8% vs poprzedni miesiąc</span>
          </div>
        </div>

        <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-white/70">Średni ROAS</span>
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <DollarSign className="w-4 h-4 text-purple-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{avgROAS}</div>
          <div className="flex items-center gap-1">
            <TrendingDown className="w-4 h-4 text-red-400" />
            <span className="text-xs text-white/50">-5% vs poprzedni miesiąc</span>
          </div>
        </div>

        <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-white/70">Całkowity koszt</span>
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <DollarSign className="w-4 h-4 text-orange-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{totalCost} PLN</div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-white/50">{totalConversions} konwersji</span>
          </div>
        </div>
      </div>

      {/* Score Distribution - DARK THEME */}
      <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-6">Rozkład wyników jakości</h3>
        <div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-green-400">Promuj (70+)</span>
                <span className="text-sm text-gray-400">{scoreDistribution.promote} słów</span>
              </div>
              <div className="w-full bg-zinc-800 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-green-600 to-emerald-600 h-3 rounded-full"
                  style={{ width: `${(scoreDistribution.promote / totalKeywords) * 100}%` }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-yellow-400">Testuj (40-69)</span>
                <span className="text-sm text-gray-400">{scoreDistribution.test} słów</span>
              </div>
              <div className="w-full bg-zinc-800 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-yellow-600 to-amber-600 h-3 rounded-full"
                  style={{ width: `${(scoreDistribution.test / totalKeywords) * 100}%` }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-red-400">Wyklucz (&lt;40)</span>
                <span className="text-sm text-gray-400">{scoreDistribution.exclude} słów</span>
              </div>
              <div className="w-full bg-zinc-800 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-red-600 to-rose-600 h-3 rounded-full"
                  style={{ width: `${(scoreDistribution.exclude / totalKeywords) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top and Bottom Performers - DARK THEME */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-green-400" />
            TOP 5 Najlepszych słów
          </h3>
          <div>
            <div className="space-y-3">
              {top5Keywords.map((keyword, index) => (
                <div key={keyword.keywordId} className="flex items-center justify-between p-3 bg-black/40 border border-white/10 rounded-lg hover:bg-black/60 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-green-400">#{index + 1}</span>
                    <div>
                      <p className="text-sm font-medium text-white">{keyword.text}</p>
                      <p className="text-xs text-gray-500">{keyword.campaignName}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-400">{keyword.totalScore}</p>
                    <p className="text-xs text-gray-500">punktów</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-6">
            <TrendingDown className="w-5 h-5 text-red-400" />
            TOP 5 Najsłabszych słów
          </h3>
          <div>
            <div className="space-y-3">
              {bottom5Keywords.map((keyword, index) => (
                <div key={keyword.keywordId} className="flex items-center justify-between p-3 bg-black/40 border border-white/10 rounded-lg hover:bg-black/60 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-red-400">#{index + 1}</span>
                    <div>
                      <p className="text-sm font-medium text-white">{keyword.text}</p>
                      <p className="text-xs text-gray-500">{keyword.campaignName}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-400">{keyword.totalScore}</p>
                    <p className="text-xs text-gray-500">punktów</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Matrix - DARK THEME */}
      <div className="bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-white mb-6">Macierz wydajności</h3>
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-black/40 rounded-lg border border-white/10 hover:bg-black/60 transition-colors">
              <p className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                {totalClicks}
              </p>
              <p className="text-sm text-gray-400 mt-1">Kliknięcia</p>
            </div>
            <div className="text-center p-4 bg-black/40 rounded-lg border border-white/10 hover:bg-black/60 transition-colors">
              <p className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                {totalConversions}
              </p>
              <p className="text-sm text-gray-400 mt-1">Konwersje</p>
            </div>
            <div className="text-center p-4 bg-black/40 rounded-lg border border-white/10 hover:bg-black/60 transition-colors">
              <p className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {totalKeywords}
              </p>
              <p className="text-sm text-gray-400 mt-1">Słowa kluczowe</p>
            </div>
            <div className="text-center p-4 bg-black/40 rounded-lg border border-white/10 hover:bg-black/60 transition-colors">
              <p className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                {totalCost > 0 ? Math.round(totalCost / totalConversions) : 0} PLN
              </p>
              <p className="text-sm text-gray-400 mt-1">Średni CPA</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceCharts;
